import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const SYMS = ["NIFTY", "BANKNIFTY", "SENSEX"];
const CLS_COLOR = {
  ATM: "text-[#22C55E] border-[#22C55E]/50", NEAR_ITM: "text-emerald-300 border-emerald-500/40",
  ITM: "text-emerald-400 border-emerald-600/40", DEEP_ITM: "text-emerald-500 border-emerald-700/40",
  NEAR_OTM: "text-amber-300 border-amber-500/40", OTM: "text-amber-400 border-amber-600/40",
  DEEP_OTM: "text-amber-500 border-amber-700/40",
};

export const QLOptionChain = () => {
  const [sym, setSym] = useState("NIFTY");
  const [ts, setTs] = useState("");
  const [win, setWin] = useState(4);
  const [data, setData] = useState(null);

  const load = () => {
    const p = new URLSearchParams({ symbol: sym, window: String(win) });
    if (ts) p.set("ts", new Date(ts).toISOString());
    api.get(`/quantlab/chain?${p}`).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [sym, win]); // eslint-disable-line

  return (
    <div className="space-y-4" data-testid="ql-option-chain">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex gap-1">
          {SYMS.map((s) => (
            <button key={s} onClick={() => setSym(s)}
                    className={`px-3 py-2 text-[11px] font-mono border ${sym === s ? "border-[#22C55E] text-white" : "border-zinc-800 text-zinc-500 hover:text-white"}`}
                    data-testid={`ql-chain-sym-${s}`}>{s}</button>
          ))}
        </div>
        <div className="text-[10px] font-mono text-zinc-500">
          <div className="mb-1">AS OF (blank = latest stored)</div>
          <input type="datetime-local" value={ts} onChange={(e) => setTs(e.target.value)}
                 className="bg-black border border-zinc-800 px-2 py-1.5 text-white" data-testid="ql-chain-ts" />
        </div>
        <div className="text-[10px] font-mono text-zinc-500">
          <div className="mb-1">ATM ± STRIKES</div>
          <select value={win} onChange={(e) => setWin(Number(e.target.value))}
                  className="bg-black border border-zinc-800 px-2 py-1.5 text-white" data-testid="ql-chain-window">
            {[2, 3, 4, 5, 6, 8, 10].map((w) => <option key={w} value={w}>±{w}</option>)}
          </select>
        </div>
        <button onClick={load} className="border border-zinc-700 px-3 py-2 text-[10px] uppercase tracking-widest font-mono text-zinc-300 hover:text-white" data-testid="ql-chain-apply">Reconstruct</button>
      </div>

      {data && !data.ok && (
        <div className="border border-[#F59E0B]/40 bg-[#F59E0B]/5 text-[#F59E0B] px-4 py-3 text-xs font-mono" data-testid="ql-chain-nodata">{data.reason}</div>
      )}

      {data?.ok && (
        <>
          <div className="flex flex-wrap gap-4 text-xs font-mono border border-zinc-800 bg-[#0a0a0a] px-4 py-3">
            <span>Spot <span className="text-white font-bold">{data.spot}</span></span>
            <span>ATM strike <span className="text-[#22C55E] font-bold" data-testid="ql-chain-atm">{data.atm_strike}</span></span>
            <span>Interval <span className="text-white">{data.strike_interval}</span></span>
            <span className="text-zinc-500">as of {new Date(data.underlying_ts).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST</span>
          </div>
          {data.note && <div className="border border-[#F59E0B]/40 bg-[#F59E0B]/5 text-[#F59E0B] px-4 py-3 text-xs font-mono" data-testid="ql-chain-nodata">{data.note}</div>}
          {data.options.length > 0 && (
            <div className="border border-zinc-800 bg-[#0a0a0a] overflow-x-auto">
              <table className="w-full text-xs font-mono">
                <thead><tr className="text-zinc-500 text-[10px] uppercase border-b border-zinc-900">
                  {["Expiry", "Strike", "Type", "Class", "Δ ATM", "Close", "Vol", "OI", "IV", "Spread", "DTE", "Mins→Exp", "Bucket"].map((h) => <th key={h} className="px-3 py-2 text-left whitespace-nowrap">{h}</th>)}
                </tr></thead>
                <tbody>
                  {data.options.map((o) => (
                    <tr key={o.contract_id} className={`border-b border-zinc-900/50 ${o.class === "ATM" ? "bg-[#22C55E]/5" : ""}`}>
                      <td className="px-3 py-1.5 text-zinc-400">{o.expiry}</td>
                      <td className="px-3 py-1.5 font-bold">{o.strike}</td>
                      <td className="px-3 py-1.5">{o.option_type}</td>
                      <td className="px-3 py-1.5"><span className={`border px-1.5 py-0.5 text-[9px] ${CLS_COLOR[o.class] || "text-zinc-400 border-zinc-700"}`}>{o.class}</span></td>
                      <td className="px-3 py-1.5">{o.strikes_from_atm > 0 ? `+${o.strikes_from_atm}` : o.strikes_from_atm}</td>
                      <td className="px-3 py-1.5 font-bold">{o.close}</td>
                      <td className="px-3 py-1.5 text-zinc-500">{o.volume ?? "—"}</td>
                      <td className="px-3 py-1.5 text-zinc-500">{o.oi ?? "—"}</td>
                      <td className="px-3 py-1.5 text-zinc-500">{o.iv ?? "—"}</td>
                      <td className="px-3 py-1.5 text-zinc-500">{o.spread ?? "—"}</td>
                      <td className="px-3 py-1.5">{o.days_to_expiry}</td>
                      <td className="px-3 py-1.5 text-zinc-500">{o.minutes_to_expiry}</td>
                      <td className="px-3 py-1.5 text-zinc-500 text-[9px]">{o.expiry_bucket}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default QLOptionChain;
